/**
 * components/ContextValidationGrid.tsx — MER Algo Footprints v7.1H
 * Hedge Fund Mode — Section 2
 *
 * Five validation cards: Structure, Liquidity, Traps, Gamma, Regime.
 * Summary-first. Gamma visually subordinate to structure/execution.
 */

import React from 'react';
import { HedgeFundModeViewModel, HFIntegrity, HFGammaPosture } from '../types/hedgeFundViewModel';

// ─── Shared card shell ────────────────────────────────────────────────────────

interface CardProps {
  title:      string;
  accent:     string;
  children:   React.ReactNode;
  secondary?: boolean;   // visually de-emphasised (used for Gamma)
}

function Card({ title, accent, children, secondary }: CardProps) {
  return (
    <div style={{
      background: 'var(--surface1)',
      border: `1px solid var(--border)`,
      borderTop: `2px solid ${secondary ? 'var(--border)' : accent}`,
      borderRadius: 6,
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      opacity: secondary ? 0.88 : 1,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, color: secondary ? 'var(--blue-dim)' : accent,
        fontFamily: 'var(--mono)', letterSpacing: 1.2, textTransform: 'uppercase',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize: 10, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{
        fontSize: 11, color: color ?? 'var(--text)',
        fontFamily: 'var(--mono)', textAlign: 'right', wordBreak: 'break-word',
      }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function Summary({ text }: { text: string }) {
  return (
    <div style={{
      fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5,
      borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 2,
    }}>
      {text}
    </div>
  );
}

// ─── Integrity badge ──────────────────────────────────────────────────────────

const INTEGRITY_CFG: Record<HFIntegrity, { color: string; label: string }> = {
  strong:  { color: '#4ade80', label: 'STRONG' },
  mixed:   { color: '#facc15', label: 'MIXED'  },
  weak:    { color: '#f87171', label: 'WEAK'   },
  unknown: { color: '#64748b', label: 'UNKNOWN'},
};

// ─── Gamma posture badge ──────────────────────────────────────────────────────

const POSTURE_CFG: Record<HFGammaPosture, { color: string; label: string }> = {
  supportive:   { color: '#4ade80', label: 'SUPPORTIVE' },
  neutral:      { color: '#94a3b8', label: 'NEUTRAL'    },
  opposing:     { color: '#f87171', label: 'OPPOSING'   },
  unavailable:  { color: '#475569', label: 'UNAVAILABLE'},
};

// ─── StructureCard ────────────────────────────────────────────────────────────

export function StructureCard({ vm }: { vm: HedgeFundModeViewModel }) {
  const s = vm.structure;
  const integ = INTEGRITY_CFG[s.integrity];
  return (
    <Card title="Structure" accent="var(--blue)">
      <Summary text={s.summary} />
      <Row label="Integrity" value={
        <span style={{ color: integ.color, fontWeight: 700, fontSize: 10 }}>{integ.label}</span>
      } />
      {s.choch && <Row label="CHOCH"    value={s.choch}           color="var(--gold)"      />}
      {s.bos   && <Row label="BOS"      value={s.bos}             color="var(--blue-muted)"/>}
      {s.premiumDiscount && <Row label="vs VWAP" value={s.premiumDiscount} />}
    </Card>
  );
}

// ─── LiquidityCard ────────────────────────────────────────────────────────────

export function LiquidityCard({ vm }: { vm: HedgeFundModeViewModel }) {
  const l = vm.liquidity;
  return (
    <Card title="Liquidity" accent="var(--purple)">
      <Summary text={l.summary} />
      {l.nearestLevel && <Row label="Nearest"  value={l.nearestLevel}  color="var(--gold)"   />}
      {l.sweepStatus  && <Row label="Sweep"    value={l.sweepStatus}                         />}
      {l.likelyTarget && <Row label="Target"   value={l.likelyTarget}  color="var(--green)"  />}
      {!l.nearestLevel && !l.likelyTarget && (
        <span style={{ fontSize: 10, color: 'var(--blue-dim)', fontFamily: 'var(--mono)' }}>
          No key levels identified
        </span>
      )}
    </Card>
  );
}

// ─── TrapCard ─────────────────────────────────────────────────────────────────

const TRAP_STATUS_COLOR: Record<string, string> = {
  confirmed:  '#f87171',
  developing: '#facc15',
  failed:     '#94a3b8',
  none:       '#475569',
};

export function TrapCard({ vm }: { vm: HedgeFundModeViewModel }) {
  const t = vm.trap;
  const statusColor = TRAP_STATUS_COLOR[t.status] ?? '#94a3b8';
  return (
    <Card title="Traps" accent="var(--amber)">
      <Summary text={t.summary} />
      <Row label="Active" value={
        <span style={{ color: t.active ? '#f87171' : '#4ade80', fontWeight: 700, fontSize: 10 }}>
          {t.active ? 'YES' : 'NO'}
        </span>
      } />
      {t.type && <Row label="Type"   value={t.type}  />}
      <Row label="Status" value={
        <span style={{ color: statusColor, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>
          {t.status}
        </span>
      } />
    </Card>
  );
}

// ─── GammaCard ────────────────────────────────────────────────────────────────

const INFLUENCE_COLOR: Record<string, string> = {
  low: '#64748b', medium: '#facc15', high: '#a78bfa', unknown: '#475569',
};

export function GammaCard({ vm }: { vm: HedgeFundModeViewModel }) {
  const g = vm.gamma;
  const posture   = POSTURE_CFG[g.posture];
  const influence = INFLUENCE_COLOR[g.influence];
  return (
    <Card title="Gamma · Secondary" accent="var(--purple)" secondary>
      <Summary text={g.summary} />
      <Row label="Posture" value={
        <span style={{ color: posture.color, fontWeight: 700, fontSize: 10 }}>{posture.label}</span>
      } />
      <Row label="Influence" value={
        <span style={{ color: influence, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>
          {g.influence}
        </span>
      } />
      {g.note && (
        <div style={{ fontSize: 10, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', lineHeight: 1.4, marginTop: 2 }}>
          {g.note}
        </div>
      )}
    </Card>
  );
}

// ─── RegimeCard ───────────────────────────────────────────────────────────────

export function RegimeCard({ vm }: { vm: HedgeFundModeViewModel }) {
  const r = vm.regime;
  return (
    <Card title="Regime" accent="var(--blue-muted)">
      <Summary text={r.summary} />
      {r.label && (
        <Row label="Regime" value={r.label} color="var(--blue)" />
      )}
      {r.tradeStyle && (
        <Row label="Style" value={r.tradeStyle} color="var(--text)" />
      )}
      {r.confidence !== null && (
        <Row label="Confidence" value={`${r.confidence}%`} color="var(--gold)" />
      )}
    </Card>
  );
}

// ─── ContextValidationGrid ────────────────────────────────────────────────────

export function ContextValidationGrid({ vm }: { vm: HedgeFundModeViewModel }) {
  return (
    <div className="hf-context-grid">
      <StructureCard vm={vm} />
      <LiquidityCard vm={vm} />
      <TrapCard      vm={vm} />
      <RegimeCard    vm={vm} />
      {/* Gamma last — visually subordinate to structure/regime */}
      <GammaCard     vm={vm} />
    </div>
  );
}
